"use client"
import { useState } from "react"
import { Send, MapPin, Phone, Mail, CheckCircle } from "lucide-react"
import NavPhc from "./NavPhc"

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  })

  const [formErrors, setFormErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState("")

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" })
    }
  }

  const validateForm = () => {
    const errors = {}
    if (!formData.name.trim()) errors.name = "Name is required"
    if (!formData.email.trim()) {
      errors.email = "Email is required"
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid"
    }
    if (!formData.message.trim()) errors.message = "Message is required"
    return errors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const errors = validateForm()
    setFormErrors(errors)
    setSubmitError("")
    setSubmitSuccess(false)

    if (Object.keys(errors).length === 0) {
      setIsSubmitting(true)
      const apiUrl = `${process.env.REACT_APP_CONTACT_US_API_URL}/api/contact`

      try {
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json", 
            Accept: "application/json" 
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            companyName: formData.company,
            message: formData.message,
            source: "MNR-Doctor-AI",
          }),
        })

        const data = await response.json()

        if (response.ok) {
          setSubmitSuccess(true)
          setFormData({
            name: "", 
            email: "", 
            phone: "", 
            company: "", 
            message: "",
          })
          setTimeout(() => setSubmitSuccess(false), 5000)
        } else {
          setSubmitError(data.message || "Failed to submit lead. Please try again.")
          console.error("Submission error:", data)
        }
      } catch (error) {
        setSubmitError("An unexpected network error occurred.")
        console.error("Error submitting form:", error)
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const inputClass = (name) =>
    `w-full px-4 py-3 bg-slate-50 border ${
      formErrors[name] ? "border-red-500" : "border-gray-200"
    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-300`

  return (
    <div className="bg-white">
      <NavPhc />
      
      {/* Dark blue header section */}
      <section className="py-24 sm:py-32 bg-[#0d1c5d] flex items-center justify-center" id="contact">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white">Get in Touch</h2>
            <p className="mt-4 text-lg text-blue-200 max-w-2xl mx-auto">
              Ready to transform your business with innovative technology solutions? Contact us today.
            </p>
          </div>
        </div>
      </section>
      
      <div className="container mx-auto px-6 pt-16 pb-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-8 bg-white p-8 sm:p-12 rounded-2xl shadow-lg">
          
          {/* Contact Information */}
          <div className="space-y-8">
            <h3 className="text-2xl font-bold text-gray-900">Contact Information</h3>
            <div className="space-y-6">
              <InfoItem icon={MapPin} title="Our Location">
                WeWork - Krishe Emerald, Ground Floor, Laxmi Cyber City,
                Whitefields, HITECH City, Hyderabad, Telangana - 500082
              </InfoItem>
              <InfoItem icon={Phone} title="Phone Number">
                +91 73299 99968
              </InfoItem>
              <InfoItem icon={Mail} title="Email Address">
                info@mnrtechnologies.com
              </InfoItem>
            </div>
            
            <div className="mt-8 rounded-2xl overflow-hidden shadow-lg h-64">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.208182181581!2d78.3601556758654!3d17.44962800108343!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb93a2a16c70cd%3A0x956024471a2a1558!2sWeWork%20-%20Krishe%20Emerald!5e0!3m2!1sen!2sin!4v1727957648439!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MNR Technologies Location"
              ></iframe>
            </div>
          </div>

          {/* Contact Form - Spans 2 columns */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} noValidate>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
              
              {submitSuccess && (
                <div className="mb-6 flex items-center gap-3 bg-green-100 text-green-800 p-4 rounded-lg">
                  <CheckCircle className="h-6 w-6" />
                  <span>Thank you for your message! We'll get back to you shortly.</span>
                </div>
              )}

              {submitError && (
                <div className="mb-6 bg-red-100 text-red-800 p-4 rounded-lg">
                  <span>{submitError}</span>
                </div>
              )}

              <div className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Full Name*</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={inputClass("name")} />
                    {formErrors.name && <p className="text-red-600 text-sm mt-1">{formErrors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address*</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={inputClass("email")} />
                    {formErrors.email && <p className="text-red-600 text-sm mt-1">{formErrors.email}</p>}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={inputClass("phone")} />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                    <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} className={inputClass("company")} />
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Your Message*</label>
                  <textarea id="message" name="message" rows="5" value={formData.message} onChange={handleChange} className={inputClass("message")}></textarea>
                  {formErrors.message && <p className="text-red-600 text-sm mt-1">{formErrors.message}</p>}
                </div>
                <div>
                  <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg shadow-lg shadow-blue-500/30 hover:bg-blue-700 disabled:bg-blue-400 transition-all duration-300 transform hover:scale-105">
                    {isSubmitting ? "Sending..." : "Send Message"}
                    {!isSubmitting && <Send size={16} />}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper component for consistent info item styling
const InfoItem = ({ icon: Icon, title, children }) => (
  <div className="flex items-start gap-5">
    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
      <Icon className="w-6 h-6 text-blue-600" />
    </div>
    <div>
      <h4 className="text-lg font-semibold text-gray-800">{title}</h4>
      <p className="text-gray-600">{children}</p>
    </div>
  </div>
)

export default Contact